// @flow
import React, { Component } from "react"
import PropTypes from "prop-types"
import { pure } from "recompose"
import styled, { ThemeProvider } from "styled-components"
import { connect } from "react-redux"
import { withRouter } from "react-router"
import { StripeProvider } from "react-stripe-elements"
import { GetCorrectEnvPkKey } from "config"
import * as eventActions from "../events/actions"
import * as bookingActions from "./actions"
import colors from "../common/components/ui/colors"
import themes from "../common/components/ui/themes"
import PopupFooter from "../common/components/popup/PopupFooter"
import { BlockingLoader } from "../common/components/loader/BlockingLoader"
import BookingDetails from "./BookingDetails"
import BookingForm from "./BookingForm"
import validate from "validate.js"
import isEmpty from "lodash/isEmpty"
import merge from "lodash/merge"
import { API_ROOT } from "../../api/constants"
import {
  getBookingDefaultState,
  getBookingConfiguration,
  SECOND_STEP
} from "../../utils/booking-config"
import { calculateVoucherDiscount } from "../../utils/price-utils"
import withLocalized from "../localization/withLocalized"
import Interpolate from "react-interpolate-component"
import { GTMEcommerce } from "../../utils/gtm-utils"
import getUrlParameter from "utils/urlParam"
import StoreCheckout from "./StoreCheckout"

import { APP_THEME } from "config"

const makeBooking = (booking: Object) => {
  const route = `${API_ROOT}bookings?${encodeURI(window.location.origin)}`
  const request = new Request(route, {
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json"
    }),
    body: JSON.stringify(booking)
  })
  return fetch(request)
    .then(response => {
      if (response.status === 200) {
        return response
      }
      throw new Error(`${response.status} ${response.statusText}`)
    })
    .then(response => response.json())
}

const initiatePayment = (paymentDetails: Object) => {
  const route = `${API_ROOT}payments?${encodeURI(window.location.origin)}`
  const request = new Request(route, {
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json"
    }),
    body: JSON.stringify(paymentDetails)
  })
  return fetch(request)
    .then(response => {
      if (response.status === 200) {
        return response
      }
      throw new Error(`${response.status} ${response.statusText}`)
    })
    .then(response => response.json())
}

const BookingContainer = styled.div`
  padding: 2.5rem 0.375rem 0;
  box-sizing: border-box;
  flex-grow: 1;
  flex-shrink: 0;
`

const StyledTerms = styled.div`
  padding: 1rem 0 0;
  font-size: 14px;
  line-height: 1.2em;
  color: #828282;
  border-top: 1px solid ${colors.BORDER_COLOR};
  p {
    margin: 0;
  }
  a {
    color: ${colors.BRAND_GREEN};
  }
`

const StoreCheckoutWrap = styled.div`
  position: absolute;
  top: 10px;
  left: 0;
  width: 100%;
  bottom: 0;
  padding: 10px;
  z-index: 2;
  background: white;
`

const StyledLabel = styled.label`
  line-height: 2rem;
  color: ${colors.GRAY_DARK};
`

const StyledCheckbox = styled.input`
  background: none;
  -webkit-appearance: none;
  border: 2px solid ${colors.BRAND_GREEN};
  width: 1rem;
  height: 1rem;
  vertical-align: middle;
  margin: -2px 0.25rem 0 0;

  &:after {
    content: "";
  }

  &:checked:after {
    content: "\u2713";
    color: ${colors.BRAND_GREEN};
    font-weight: bold;
    text-align: center;
    width: 100%;
    display: block;
    vertical-align: middle;
    height: 100%;
    line-height: 100%;
    font-size: 0.8rem;
  }
`

class Booking extends Component {
  form: HTMLFormElement
  stripeElement: HTMLElement

  static propTypes = {
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  }

  static defaultProps = {}

  state

  config

  constructor() {
    super()
    const defaultState = getBookingDefaultState()
    this.state = {
      ...defaultState,
      userData: { ...defaultState.userData, payment_type: undefined }
    }
    this.config = getBookingConfiguration()
    this.config[SECOND_STEP].fields.push({
      key: "voucher",
      fieldType: "voucher", // -> constants
      label: "booking.form.voucher",
      props: {
        onCodeValid: this._calculatePricingWithVoucher,
        onCodeInvalid: this._removeVoucherPricing,
        getVoucherPrice: this._getVoucherPrice,
        getVoucherMetaData: this._getVoucherMetaData,
        getVoucherCustomerObject: this._getVoucherCustomerObject
      }
    })
  }

  componentWillMount() {
    this.loadData()
  }

  componentDidUpdate(prevProps, prevState) {
    const { event } = this.props
    if (event !== prevProps.event) {
      this.updateData()
    }
  }

  _calculatePricingWithVoucher = result => {
    this.setState(
      {
        voucherDiscount: result
      },
      () => {
        setTimeout(() => {
          this.updateData()
        }, 50)
      }
    )
  }

  _removeVoucherPricing = () => {
    this.setState(
      {
        voucherDiscount: null
      },
      () => {
        setTimeout(() => {
          this.updateData()
        }, 50)
      }
    )
  }

  _getVoucherPrice = () => {
    const { price } = this.state
    if (price) {
      return price.price_value_original
        ? price.price_value_original
        : price.price_value
    } else {
      return undefined
    }
  }

  _getVoucherMetaData = () => {
    const { event } = this.props
    const { userData, currency } = this.state
    return {
      organizer: event ? parseInt(event.pro[0].id, 10) : -1,
      bookingType: event.type && event.type[0],
      email: userData.email,
      currency: currency.toUpperCase()
    }
  }

  _getVoucherCustomerObject = () => {
    const { userData } = this.state
    return {
      name: `${userData.first_name} ${userData.last_name}`,
      source_id: userData.email,
      email: userData.email
    }
  }

  updateData() {
    const { event } = this.props
    const price = this.determinePrice(event)
    if (this.state.voucherDiscount) {
      this.setState({
        price: calculateVoucherDiscount(price, this.state.voucherDiscount),
        currency: event.currency,
        vat: event.vat,
        isLoading: false
      })
    } else {
      this.setState({
        price,
        currency: event.currency,
        vat: event.vat,
        isLoading: false
      })
    }
  }

  loadData() {
    const { id, loadEvent } = this.props
    loadEvent(id).then(() => {
      setTimeout(() => {
        this.updateData()
      }, 50)
    })
  }

  determinePrice = event => {
    const { priceID } = this.props
    if (priceID === undefined) return

    return (
      event.prices.filter(
        price => parseInt(price.id, 10) === parseInt(priceID, 10)
      )[0] || ""
    )
  }

  handleChange = (event: Event) => {
    if (
      !(
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement
      )
    )
      return
    const field = event.target.name
    const userData = this.state.userData
    userData[field] = event.target.value

    const errors =
      validate(userData, this.config[this.state.step].constraints) || {}
    const touchedFields = [
      ...this.state.touchedFields.filter(item => item !== field),
      field
    ]

    const isValid = isEmpty(errors)

    this.setState({
      errors,
      touchedFields,
      isValid,
      userData
    })
  }

  _initializeStripePayment({
    order_number,
    success_url,
    price,
    client_secret
  }) {
    this.setState(
      {
        stripe_order_number: order_number,
        stripe_success_url: success_url,
        stripe_client_secret: client_secret
      },
      () => {
        if (parseInt(price, 10) !== 0) {
          //this.stripeElement.click();
        } else {
          // zero price, paid with voucher
          this._onStripeToken({})
        }
      }
    )
  }

  _onStripeToken = data => {
    const { history } = this.props
    data.booking_id = this.state.stripe_order_number

    this.setState({ stripePaymentProcessing: true })

    const request = new Request(this.state.stripe_success_url, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json"
      }),
      body: JSON.stringify(data)
    })
    return fetch(request)
      .then(response => {
        this.setState(
          {
            stripePaymentProcessing: false
          },
          () => {
            if (response.status === 200) {
              const transaction = GTMEcommerce.formEventTransactionPayload(
                data.booking_id,
                this.state.price,
                this.props.event
              )
              GTMEcommerce.asyncGTMEcommerceTrack(transaction)
              history.push(`${this.props.location.pathname}/complete`)
            } else {
              history.push(`${this.props.location.pathname}/error`)
            }
          }
        )
      })
      .catch(() => {
        alert(
          "There was a problem processing the payment. Please try again later."
        )
      })
  }

  _onStripeClose = () => {
    this.setState({
      isSubmitting: false,
      isLoading: false
    })
  }

  _resolveLocaleFromLocalization() {
    const { localeConfig } = this.props
    const locale = localeConfig("locale")
    return locale.substring(0, locale.indexOf("_")) === "en" ? "en" : "auto"
  }

  _resolveCurrency() {
    const { localeConfig } = this.props
    const { currency } = this.state
    return currency || localeConfig("units.currency.identifier")
  }

  _handlePaymentAction = response => {
    const { order_number, payment_url, payment_gateway, price } = response

    if (payment_url) {
      const transaction = GTMEcommerce.formEventTransactionPayload(
        order_number,
        this.state.price,
        this.props.event
      )
      GTMEcommerce.pageLoadGTMEcommerceTrack(transaction)

      /*
        If payment was made with voucher the user no longer needs to be redirected
        to the payment service. Instead we call backend with verification and continue
        the user to success-page.
       */
      if (price === 0) {
        fetch(payment_url).then(() => {
          this.props.history.push(`${this.props.location.pathname}/complete`)
        })
      } else {
        window.location.href = payment_url
      }
    } else if (payment_gateway === "stripe") {
      if (order_number !== undefined) {
        this._initializeStripePayment(response)
      }
    }
  }

  handleSubmit = (event: Event) => {
    event.preventDefault()
    if (!this.state.isValid) return

    const step = this.state.step
    if (step === 0) {
      this.setState({
        step: step + 1,
        isValid: false
      })
      return
    }

    let sales_channel = "web"
    if (getUrlParameter("saleschannel")) {
      sales_channel = getUrlParameter("saleschannel")
    }

    const receive_newsletter = document.querySelector("#opt-in").checked ? 1 : 0
    const handicap = this.state.userData.handicap // !== '-' ? this.state.userData.handicap : '';

    const postData = merge(
      {
        event_id: this.props.id,
        price_id:
          this.state.price && this.state.price.id ? this.state.price.id : "",
        price_revision_id:
          this.state.price && this.state.price.revision_id
            ? this.state.price.revision_id
            : "",
        receive_newsletter,
        voucher_code:
          (this.state.voucherDiscount && this.state.voucherDiscount.code) || "",
        sales_channel
      },
      this.state.userData,
      { handicap }
    )

    this.setState({
      isSubmitting: true
    })

    makeBooking(postData)
      .then(({ id }) => {
        if (id !== undefined) {
          initiatePayment({
            booking_id: id,
            success_url: `${window.location.href}/complete`,
            failure_url: `${window.location.href}/error`
          })
            .then(this._handlePaymentAction)
            .catch(() => {
              this.props.history.replace(
                `${this.props.location.pathname}/error`
              )
            })
        }
      })
      .catch(() => {
        this.props.history.replace(`${this.props.location.pathname}/error`)
      })
  }

  render() {
    const { localized } = this.props

    if (this.state.hasFetchError) {
      return <p>{localized("booking.form.fetch_error")}</p>
    }
    const stepSubmitLabel =
      this.state.step === 0
        ? localized("booking.form.label_next")
        : localized("booking.form.label_submit")
    const fields = this.config[this.state.step].fields
    const shouldShowTermsLink = this.state.step === 1
    const shouldFocusFirstField = false

    return (
      <div>
        {(
          !this.state.isLoading &&
          this.props.event &&
          this.props.event.pro &&
          this.props.event.pro.length
        ) && (
          <StripeProvider
            apiKey={GetCorrectEnvPkKey()}
            stripeAccount={this.props.event.pro[0].stripe_account_id}
          >
            <BookingContainer>
              <BookingDetails
                event={this.props.event}
                price={this.state.price}
                currency={this.state.currency}
                vat={this.state.vat}
              />
              <ThemeProvider theme={themes[APP_THEME]}>
                <div>
                  <BookingForm
                    disabled={!this.state.isValid || this.state.isSubmitting}
                    fields={fields}
                    touchedFields={this.state.touchedFields}
                    errors={this.state.errors}
                    onChange={this.handleChange}
                    onSubmit={this.handleSubmit}
                    submitLabel={stepSubmitLabel}
                    shouldFocusFirstField={shouldFocusFirstField}
                  >
                    {shouldShowTermsLink && (
                      <StyledTerms>
                        <p>
                          <Interpolate
                            with={{
                              terms: (
                                <a href="/golfhubber_terms.pdf" target="_blank">
                                  {localized("booking.form.label_terms_link")}
                                </a>
                              )
                            }}
                            format={localized("booking.form.label_terms")}
                          />
                          <br />
                          <StyledLabel htmlFor="opt-in">
                            <StyledCheckbox
                              type="checkbox"
                              name="opt-in"
                              id="opt-in"
                            />{" "}
                            {localized(
                              "booking.form.label_newsletter_subscription"
                            )}
                          </StyledLabel>
                        </p>
                      </StyledTerms>
                    )}
                  </BookingForm>
                  {this.state.stripe_client_secret && (
                    <StoreCheckoutWrap>
                      <StoreCheckout
                        loading={this.state.stripePaymentProcessing}
                        onSuccess={data => {
                          this._onStripeToken(data)
                        }}
                        client_secret={this.state.stripe_client_secret}
                        user={this.state.userData}
                      />
                    </StoreCheckoutWrap>
                  )}
                </div>
              </ThemeProvider>
              <PopupFooter
                linkLabel={localized("booking.form.label_help")}
                linkTarget={{
                  pathname: `${this.props.match.url}/help`,
                  state: { modal: true }
                }}
              />
            </BookingContainer>
          </StripeProvider>
        )}
        <BlockingLoader
          isLoading={
            this.state.isLoading ||
            this.state.isSubmitting ||
            this.state.stripePaymentProcessing
          }
        />
      </div>
    )
  }
}

const mapStateToProps = (state, { match }) => {
  const id = match.params.id
  const priceID = match.params.priceid
  const {
    entities: { events }
  } = state

  return {
    event: events[id] || undefined,
    id,
    priceID
  }
}

Booking = withRouter(
  connect(
    mapStateToProps,
    Object.assign({}, eventActions, bookingActions)
  )(Booking)
)

export default pure(withLocalized(Booking))



// WEBPACK FOOTER //
// ./src/modules/booking/Booking.js